/**
   * @prop --stzh-pi-pagetitle-background-color: Background if it has a menu
   * @prop --stzh-pi-pagetitle-hero-background-color: Background color
   * @prop --stzh-pi-pagetitle-lead-background-color: Background of lead section
 */

:host {
  --hero-background-color: var(--stzh-pi-pagetitle-hero-background-color, #{$colorPrimary10});
  --lead-background-color: var(--stzh-pi-pagetitle-lead-background-color, #{$colorPrimary40});
  --background-color: var(--stzh-pi-pagetitle-background-color, #{$colorCoolgrey10});
  --grid-template-areas: "hero hero hero hero" "image-lead-menu image-lead-menu image-lead-menu image-lead-menu";

  @include mq($from: medium) {
    --grid-template-areas: ". . hero hero hero hero . ." "image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu";
  }

  @include mq($from: large) {
    --grid-template-areas: ". . . hero hero hero hero hero hero . . ." "image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu image-lead-menu";
  }

  ::slotted([slot="dateline"]) {
    @include fontSize('milli');
    @include font('heavy');
    --color: var(--stzh-color-black80op);
  }

  ::slotted([slot="heading"]),
  ::slotted([slot="lead"]) *,
  ::slotted([slot="breadcrumb"]) {
    --color: var(--stzh-color-black);
  }

  ::slotted([slot="menu"]) {
    --stzh-pi-menu-padding-left: #{$containerMargin};

    @include mq($from: small) {
      --stzh-pi-menu-padding-left: #{$containerMarginSmall};
    }

    @include mq($from: large) {
      --stzh-pi-menu-padding-left: #{$containerMarginLarge};
    }

    @include mq($from: ultra) {
      --stzh-pi-menu-padding-left: calc((100vw - #{$containerMaxWidth}) / 2);
    }
  }
}

.stzh-pi-pagetitle {
  @include spaceCurve('margin-bottom', 'large');


  /* Containers */

  &__hero-container {
    @include grid;
    grid-template-areas: var(--grid-template-areas);
    background-color: var(--hero-background-color);
    @include spaceCurve('padding-top', 'regular');
    @include containerPadding;
  }

  &__image-lead-menu-container {
    @include grid;
    grid-template-areas: var(--grid-template-areas);
    background-color: var(--hero-background-color);

    @include mq($from: medium) {
      @include containerPadding;
    }
  }

  &__lead-container {
    @include grid;
    grid-template-areas: "lead lead lead lead";
    background-color: var(--lead-background-color);
    @include containerPadding;

    @include mq($from: medium) {
      grid-template-areas: ". . lead lead lead lead lead lead";
    }

    @include mq($from: large) {
      grid-template-areas: ". . . lead lead lead lead lead lead . . .";
    }
  }

  &__image-lead-menu {
    grid-area: image-lead-menu;

    @include grid;
    grid-template-areas: "image image image image" "lead lead lead lead";

    @include mq($from: medium) {
      grid-template-areas: ". . image image image image image image" "lead lead lead lead lead lead lead lead";
    }

    @include mq($from: large) {
      grid-template-areas: ". . . image image image image image image image image image" "lead lead lead lead lead lead lead lead lead lead lead lead";
    }
  }

  /* Elements */

  &__hero {
    grid-area: hero;
  }

  &__menu {
    display: none;
    grid-area: menu;
    z-index: 2;
  }

  &__image {
    grid-area: image;

    @include mq($from: medium) {
      margin-right: -$containerMarginMedium;
    }

    @include mq($from: large) {
      margin-right: -$containerMarginLarge;
    }

    @include mq($from: ultra) {
      margin-right: calc((100vw - #{$containerMaxWidth}) / -2)
    }
  }

  &__lead {
    --stzh-base-lead-color: #{$headingColor};
    grid-area: lead;
    background-color: var(--lead-background-color);
    @include spaceCurve('padding-top', 'regular');
    @include spaceCurve('padding-bottom', 'regular');
  }

  &__text {
    @include spaceCurve('padding-top', 'big');
    @include spaceCurve('padding-bottom', 'big');
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__meta {
    display: flex;
    flex-direction: column-reverse;
    row-gap: space('xsmall');

    @include mq($from: medium) {
      flex-direction: row;
    }
  }

  &__readspeaker {
    margin-bottom: space('medium');

    @include mq($from: small) {
      margin-bottom: space('large');
    }

    @include mq($from: small, $to: medium) {
      --stzh-readspeaker-dropdown-position: relative;
      --stzh-readspeaker-dropdown-top: auto;
    }
  }


  &__dateline {
    margin-bottom: space('xxsmall');
  }

  &__breadcrumbs {
    @include spaceCurve('margin-bottom', 'regular');
  }

  &__heading {
    @include spaceCurve('margin-bottom', 'regular');
  }

  /**
   * Menu.
   */

  &__menu-inner {
    @include mq($from: medium) {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    }
  }


  /* Article Info */
  &__article-info-wrapper {
    @include container();
    @include spaceCurve('padding-top', 'large');
    @include grid;
    grid-template-areas: "article-info article-info article-info article-info";

    @include mq($from: medium) {
      grid-template-areas: ". . article-info article-info article-info article-info article-info article-info";
    }

    @include mq($from: large) {
      grid-template-areas: ". . . article-info article-info article-info article-info article-info article-info article-info article-info article-info";
    }
  }

  &__article-info {
    grid-area: article-info;
  }

  &__author {
    margin-top: 0;
    margin-bottom: var(--stzh-space-xxsmall);
    color: var(--stzh-color-coolgrey70);
    @include font('heavy');
    @include fontCurve('p1');
  }

  &__date {
    margin: 0;
    @include fontCurve('p1');
  }

  /**
   * Has menu.
   */
  &--has-menu &__image-lead-menu {
    grid-template-columns: auto repeat(3, minmax(0, 1fr));
    grid-template-areas: "menu image image image" "menu lead lead lead";

    @include mq($from: medium) {
      grid-template-columns: $gridColumnsMedium;
      grid-template-areas: "menu menu image image image image image image" "menu menu lead lead lead lead lead lead";
    }

    @include mq($from: large) {
      grid-template-columns: $gridColumnsLarge;
      grid-template-areas: "menu menu menu image image image image image image image image image" "menu menu menu lead lead lead lead lead lead lead lead lead";
    }
  }

  &--has-menu &__image-lead-menu-container {
    background-color: var(--background-color);
  }

  &--has-menu &__menu {
    position: relative;
    display: inherit;
    margin-right: calc(#{$gridGutter} * -1);

    @include mq($from: small) {
      margin-right: calc(#{$gridGutterSmall} * -1);
    }

    @include mq($from: medium) {
      margin-right: calc(#{$gridGutterMedium} * -1);
      margin-left: -$containerMarginMedium;
    }

    @include mq($from: large) {
      margin-right: calc(#{$gridGutterLarge} * -1);
      margin-left: -$containerMarginLarge;
    }

    @include mq($from: ultra) {
      margin-right: calc(#{$gridGutterUltra} * -1);
      margin-left: calc((100vw - #{$containerMaxWidth}) / -2);
    }
  }

  &--has-menu &__lead {
    @include spaceCurve('padding-left', 'regular');

    @include mq($from: medium) {
      margin-right: -$containerMarginMedium;
      padding-right: $containerMarginMedium;
    }

    @include mq($from: large) {
      margin-right: -$containerMarginLarge;
      padding-right: $containerMarginLarge;
    }

    @include mq($from: ultra) {
      margin-right: calc((100vw - #{$containerMaxWidth}) / -2);
      padding-right: calc((100vw - 84.25rem) / 2);
    }
  }

  /* Has meta */

  &--has-meta &__text {
    @include spaceCurve('padding-top', 'regular');
  }

  /* Level 3-4 */
  &--level-3,
  &--level-4 {
    --hero-background-color: var(--stzh-color-coolgrey20);
    --lead-background-color: var(--stzh-color-coolgrey30);
  }

  /* Hyphens */
  &--hyphens &__heading {
    hyphens: auto;
  }
}

